import React, { useState } from 'react';
import Modal from './Modal';
import { UI_DESIGN_OPTIONS } from '../constants/bookingConst';
import { resolveUiDesignOption } from '../utils/uiDesignUtil';
import '../css/UiDesignSettings.css';

/**
 * Config -> Theme -> UI Design: which design the guest booking screen uses,
 * with a thumbnail of the selected version that opens a full-size preview.
 *
 * Edits only the form — nothing reaches guests until Save Configuration and a
 * redeploy, and the save is where the change is written to the audit trail.
 *
 * @param {object} props
 * @param {string|undefined} props.value - formData.uiDesignVersion
 * @param {(next: string) => void} props.onChange - receives the picked version value
 * @returns {JSX.Element}
 */
function UiDesignSettings({ value, onChange }) {
  const [previewOpen, setPreviewOpen] = useState(false);
  // Which preview image failed to load, so the thumbnail can say so instead
  // of showing a broken-image box.
  const [failedPreviewUrl, setFailedPreviewUrl] = useState(null);
  const selected = resolveUiDesignOption(value);
  const previewFailed = failedPreviewUrl === selected.previewUrl;

  return (
    <div className="uid-section">
      <h3 className="uid-title">UI Design</h3>
      <p className="uid-desc">
        The design of the guest booking screen. Changes apply once you press &quot;Save Configuration&quot; and redeploy.
      </p>

      <div className="uid-body">
        <div className="uid-field">
          <label className="uid-label" htmlFor="uid-version-select">Version</label>
          <select
            id="uid-version-select"
            className="uid-select"
            value={selected.value}
            onChange={(e) => onChange(e.target.value)}
          >
            {UI_DESIGN_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <p className="uid-option-desc">{selected.description}</p>
        </div>

        <button
          type="button"
          className="uid-thumb"
          onClick={() => setPreviewOpen(true)}
          disabled={previewFailed}
          aria-label={`Preview ${selected.label}`}
        >
          {previewFailed ? (
            <span className="uid-thumb-missing">Preview unavailable</span>
          ) : (
            <img
              key={selected.previewUrl}
              src={selected.previewUrl}
              alt={`${selected.label} booking screen`}
              onError={() => setFailedPreviewUrl(selected.previewUrl)}
            />
          )}
          {!previewFailed && <span className="uid-thumb-hint">Click to preview</span>}
        </button>
      </div>

      <Modal
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title={`${selected.label} preview`}
        hideIcon
        confirmText="Close"
        className="uid-preview-modal"
      >
        <img className="uid-preview-img" src={selected.previewUrl} alt={`${selected.label} booking screen`} />
      </Modal>
    </div>
  );
}

export default UiDesignSettings;
